import React from 'react';
import { ArrowLeft, FileText, Mail, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useTrackLegalPageView } from '../hooks/useTrackLegalPageView';

const TermsOfService = () => {
  const { isDarkMode } = useTheme();
  const currentDate = "August 2026";
  
  // Track page view
  useTrackLegalPageView('terms-of-service');
  
  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b ${isDarkMode ? 'bg-gray-900/95 border-gray-800' : 'bg-white/95 border-gray-200'} backdrop-blur-sm`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <Link 
            to="/" 
            className={`inline-flex items-center gap-2 text-sm font-medium ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors`}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Title */}
        <div className="text-center mb-12">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 ${isDarkMode ? 'bg-cyan-500/20' : 'bg-cyan-100'}`}>
            <FileText className={`w-8 h-8 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
          </div>
          <h1 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Terms of Service
          </h1>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Last updated: {currentDate}
          </p>
        </div>

        {/* Policy Content */}
        <div className={`prose prose-lg max-w-none ${isDarkMode ? 'prose-invert' : ''}`}>
          
          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>1. Agreement to Terms</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              By accessing or using the services provided by NEURA AI SOFTWARES ("Company", "we", "our", or "us"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our services.
            </p>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              These Terms apply to all visitors, users, and clients who access or use our website and services, including but not limited to web design, AI solutions, SEO services, and digital marketing.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>2. Services</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              NEURA AI SOFTWARES provides the following services:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>Custom website design and development</li>
              <li>Custom software development (mobile apps and desktop applications)</li>
              <li>AI-powered business solutions and automation</li>
              <li>Search engine optimisation (SEO)</li>
              <li>Google Business Profile setup and management</li>
              <li>CRM integration and lead management</li>
              <li>Digital marketing and email campaigns</li>
              <li>Ongoing website maintenance and support</li>
            </ul>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              The specific scope of services will be outlined in individual service agreements or proposals provided to each client.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>3. Use of AI Assistance in Development</h2>
            <div className={`p-5 rounded-xl mb-4 ${isDarkMode ? 'bg-purple-900/20 border border-purple-600/30' : 'bg-purple-50 border border-purple-200'}`}>
              <p className={`font-semibold mb-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>Important Disclosure</p>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                NEURA AI SOFTWARES utilises artificial intelligence (AI) tools and assistance in the development of some or all parts of client projects. This approach enables us to:
              </p>
            </div>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li><strong>Minimize the gap</strong> between idea creation and actual project completion</li>
              <li><strong>Accelerate development timelines</strong> while maintaining quality standards</li>
              <li><strong>Reduce costs</strong> and deliver more value to our clients</li>
              <li><strong>Enhance creativity</strong> by leveraging AI capabilities alongside human expertise</li>
            </ul>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              By engaging our services, you acknowledge and agree that:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>AI assistance may be used in code generation, design creation, content writing, and other aspects of project development</li>
              <li>All AI-generated work is reviewed, tested, and quality-assured by our human team before delivery</li>
              <li>The final deliverables remain our responsibility and are covered by our standard warranties</li>
              <li>You will receive the same level of support regardless of how the work was produced</li>
            </ul>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              This disclosure is provided in the spirit of transparency. Our use of AI tools reflects our commitment to innovation and efficient service delivery.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>4. Client Responsibilities</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              As a client, you agree to:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>Provide accurate and complete information required for service delivery</li>
              <li>Respond to requests for content, feedback, or approvals in a timely manner</li>
              <li>Ensure you have the rights to any content (text, images, logos) you provide to us</li>
              <li>Make payments according to the agreed payment schedule</li>
              <li>Not use our services for any unlawful purposes</li>
              <li>Maintain the confidentiality of any login credentials we provide</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>5. Payment Terms</h2>
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>5.1 Pricing</h3>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              All prices are quoted in British Pounds (GBP) unless otherwise stated. Prices are exclusive of VAT where applicable.
            </p>
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>5.2 Payment Schedule</h3>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li><strong>One-time projects:</strong> 50% deposit required before work begins, with the balance due upon completion</li>
              <li><strong>Monthly subscriptions:</strong> Billed monthly in advance via Direct Debit or card payment</li>
              <li><strong>Custom arrangements:</strong> As specified in your individual service agreement</li>
            </ul>
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>5.3 Late Payments</h3>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Invoices not paid within 14 days of the due date may incur a late payment fee of 5% of the outstanding amount. We reserve the right to suspend services until payment is received.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>6. Intellectual Property</h2>
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>6.1 Client Content</h3>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              You retain ownership of all content you provide to us (logos, text, images, etc.). By providing content, you grant us a licence to use it for the purpose of delivering our services.
            </p>
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>6.2 Our Work</h3>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Upon full payment, you will own the custom website design and content created specifically for your project. We retain ownership of:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>Our proprietary tools, templates, and AI systems</li>
              <li>General methodologies and techniques</li>
              <li>Any third-party software or plugins used (subject to their own licences)</li>
            </ul>
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>6.3 Portfolio Rights</h3>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              We reserve the right to display completed work in our portfolio and marketing materials unless you request otherwise in writing.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>7. Website Hosting & Domains</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              For clients using our monthly website packages:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>We will host your website on our managed servers</li>
              <li>Domain registration and renewal are included in the monthly fee (for £100/month packages)</li>
              <li>For one-time payment packages, you are responsible for purchasing and renewing your own domain</li>
              <li>We will maintain regular backups of your website</li>
              <li>Hosting services are contingent on active payment of your subscription</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>8. Service Level & Support</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              We aim to provide:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>99.9% website uptime (excluding scheduled maintenance)</li>
              <li>Response to support requests within 24-48 business hours</li>
              <li>Regular security updates and maintenance</li>
              <li>Monthly performance reports for applicable packages</li>
            </ul>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Support is available Monday to Friday, 9am to 5pm GMT, excluding UK bank holidays.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>9. Cancellation & Refunds</h2>
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>9.1 Monthly Subscriptions</h3>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              You may cancel your monthly subscription at any time with 30 days' written notice. No refunds will be given for partial months.
            </p>
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>9.2 One-Time Projects</h3>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              If you cancel a project after work has begun:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>Deposits are non-refundable</li>
              <li>You will be billed for work completed to date</li>
              <li>Any unused portion may be refunded at our discretion</li>
            </ul>
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>9.3 Cooling-Off Period</h3>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Consumer clients have a 14-day cooling-off period under the Consumer Contracts Regulations 2013. If you request that work begins within this period, you may be charged for services already provided.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>10. Limitation of Liability</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              To the maximum extent permitted by law:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>Our total liability for any claim shall not exceed the total fees paid by you in the 12 months preceding the claim</li>
              <li>We are not liable for any indirect, incidental, or consequential damages</li>
              <li>We are not responsible for any loss of data, revenue, or business opportunities</li>
              <li>We do not guarantee specific results from SEO or marketing services</li>
            </ul>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Nothing in these Terms excludes or limits our liability for death, personal injury caused by negligence, fraud, or any other liability that cannot be excluded by law.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>11. Confidentiality</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Both parties agree to keep confidential any proprietary information shared during our working relationship. This includes business strategies, customer data, and technical specifications. This obligation continues for 2 years after our agreement ends.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>12. Termination</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              We may terminate or suspend your access to our services immediately, without prior notice, if:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>You breach any provision of these Terms</li>
              <li>Payment is not received after reasonable attempts to collect</li>
              <li>You engage in fraudulent or illegal activity</li>
              <li>Continuing the relationship would harm our reputation or other clients</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>13. Governing Law</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              These Terms shall be governed by and construed in accordance with the laws of Northern Ireland and the United Kingdom. Any disputes shall be subject to the exclusive jurisdiction of the courts of Northern Ireland.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>14. Changes to Terms</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              We reserve the right to modify these Terms at any time. We will notify clients of material changes via email or through our website. Continued use of our services after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>15. Contact Information</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              If you have any questions about these Terms, please contact us:
            </p>
            <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <p className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>NEURA AI SOFTWARES</p>
              <div className="space-y-3">
                <div className={`flex items-center gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <MapPin className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                  <span>Belfast, Northern Ireland, United Kingdom</span>
                </div>
                <div className={`flex items-center gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <Mail className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                  <a href="mailto:PeterG@neura-ai.info" className="hover:text-cyan-500 transition-colors">
                    PeterG@neura-ai.info
                  </a>
                </div>
              </div>
            </div>
          </section>

        </div>

        {/* Back to Home */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700 text-center">
          <Link 
            to="/" 
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-colors ${isDarkMode ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className={`border-t py-6 ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            © {new Date().getFullYear()} NEURA AI SOFTWARES. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
